const agentFetch = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    credentials: 'include', // portal session cookie for CHAT_AGENT / CHAT_SUPERVISOR users
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `Request failed with status ${res.status}`);
  }
  if (res.status === 204) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

export const agentService = {
  async getPendingConversations() {
    return agentFetch('/api/agent/inbox/pending');
  },

  async getMyConversations() {
    return agentFetch('/api/agent/inbox/mine');
  },

  async pickup(sessionId) {
    return agentFetch(`/api/agent/inbox/${encodeURIComponent(sessionId)}/pickup`, { method: 'POST' });
  },

  async reply(sessionId, message, mediaUrl = null) {
    return agentFetch('/api/agent/inbox/reply', {
      method: 'POST',
      body: JSON.stringify({ sessionId, message: message || '', mediaUrl }),
    });
  },

  async uploadImage(file) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch('/api/agent/inbox/upload-image', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Image upload failed');
    }
    return res.json(); // { url: '/chat-images/...' }
  },

  async resolve(sessionId, resolutionStatus = null, note = null) {
    const body = {};
    if (resolutionStatus) body.resolutionStatus = resolutionStatus;
    if (note) body.note = note;
    return agentFetch(`/api/agent/inbox/${encodeURIComponent(sessionId)}/resolve`, {
      method: 'POST',
      ...(Object.keys(body).length ? { body: JSON.stringify(body) } : {}),
    });
  },

  async getMessagesSince(sessionId, sinceId = 0) {
    return agentFetch(`/api/chat/${encodeURIComponent(sessionId)}/messages?sinceId=${sinceId}`);
  },

  async getMyHandledConversations(page = 0, size = 20, status = null, search = null) {
    const params = new URLSearchParams({ page, size });
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    return agentFetch(`/api/agent/analytics/handled?${params.toString()}`);
  },

  async getMySummary() {
    return agentFetch('/api/agent/analytics/summary');
  },

  async getConversationMessages(sessionId) {
    return agentFetch(`/api/agent/conversations/${encodeURIComponent(sessionId)}/messages`);
  },

  async addCallbackUpdate(sessionId, note) {
    return agentFetch(`/api/agent/conversations/${encodeURIComponent(sessionId)}/callback-update`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  },

  async closeCallbackConversation(sessionId, note = '', status = 'SATISFIED') {
    return agentFetch(`/api/agent/conversations/${encodeURIComponent(sessionId)}/close-callback`, {
      method: 'POST',
      body: JSON.stringify({ note, status }),
    });
  },
};
